import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { CarrierSchema } from "../fulfillments/fulfillment-carriers-and-services";

export enum SurchargeDisputeStatus {
  OPEN = "open",
  SUBMITTED_TO_CARRIER = "submitted_to_carrier",
  ACCEPTED = "accepted",
  REJECTED = "rejected",
}

export const surchargeDisputeStatusLabels: Record<
  SurchargeDisputeStatus,
  string
> = {
  [SurchargeDisputeStatus.OPEN]: "Open",
  [SurchargeDisputeStatus.SUBMITTED_TO_CARRIER]: "Submitted to carrier",
  [SurchargeDisputeStatus.ACCEPTED]: "Accepted",
  [SurchargeDisputeStatus.REJECTED]: "Rejected",
};

/**
 * Statuses that are admin-internal but appear as "Open" to the merchant.
 */
export const adminOnlySurchargeDisputeStatuses: SurchargeDisputeStatus[] = [
  SurchargeDisputeStatus.SUBMITTED_TO_CARRIER,
];

export function getMerchantFacingSurchargeDisputeStatus(
  status: SurchargeDisputeStatus,
): SurchargeDisputeStatus {
  if (adminOnlySurchargeDisputeStatuses.includes(status)) {
    return SurchargeDisputeStatus.OPEN;
  }
  return status;
}

export const merchantFacingSurchargeDisputeStatusLabels: Record<
  SurchargeDisputeStatus,
  string
> = {
  [SurchargeDisputeStatus.OPEN]: "Open",
  [SurchargeDisputeStatus.SUBMITTED_TO_CARRIER]: "Open",
  [SurchargeDisputeStatus.ACCEPTED]: "Accepted",
  [SurchargeDisputeStatus.REJECTED]: "Rejected",
};

const SurchargeDisputeSchema_ = z.object({
  id: z.string(),
  accountId: z.string(),
  balanceTransactionId: z.string(),
  status: createEnum(SurchargeDisputeStatus, {
    enumName: "SurchargeDisputeStatus",
  }),
  reason: z.string().nullish(),
  attachments: z.array(z.string()).nullish(),
  isRefunded: z.boolean(),
  refundAmount: z.string().nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const SurchargeDisputeSchema: z.ZodType<SurchargeDispute> =
  SurchargeDisputeSchema_;
export interface SurchargeDispute
  extends z.infer<typeof SurchargeDisputeSchema_> {}

const ChangeSchema_ = z.object({
  label: z.string(),
  old: z.string(),
  new: z.string(),
});

const ParcelChangesSchema_ = z.object({
  trackingNumber: z.string().optional(),
  changes: z.array(ChangeSchema_),
});

// Combined type with data joined from balance transaction and fulfillment group
const SurchargeDisputeDataSchema_ = SurchargeDisputeSchema_.extend({
  description: z.string(),
  amount: z.string(),
  fulfillmentGroupId: z.string().nullish(),
  carrier: CarrierSchema.nullish(),
  teamName: z.string().nullish(),
  trackingCode: z.string().nullish(),
  surchargeDescriptions: z.array(z.string()),
  parcelChanges: z.array(ParcelChangesSchema_),
});

export const SurchargeDisputeDataSchema: z.ZodType<SurchargeDisputeData> =
  SurchargeDisputeDataSchema_;
export interface SurchargeDisputeData
  extends z.infer<typeof SurchargeDisputeDataSchema_> {}

const SurchargeDisputeUpdateSchema_ = z.object({
  id: z.string(),
  status: createEnum(SurchargeDisputeStatus, {
    enumName: "SurchargeDisputeStatus",
  }).optional(),
  internalNote: z.string().nullish(),
  reason: z.string().nullish(),
  refundAmount: z.string().nullish(),
});

export const SurchargeDisputeUpdateSchema: z.ZodType<SurchargeDisputeUpdate> =
  SurchargeDisputeUpdateSchema_;
export interface SurchargeDisputeUpdate
  extends z.infer<typeof SurchargeDisputeUpdateSchema_> {}

const CreateSurchargeDisputeSchema_ = z.object({
  balanceTransactionId: z.string(),
  reason: z.string().nullish(),
  attachments: z.array(z.string()).nullish(),
});

export const CreateSurchargeDisputeSchema: z.ZodType<CreateSurchargeDispute> =
  CreateSurchargeDisputeSchema_;
export interface CreateSurchargeDispute
  extends z.infer<typeof CreateSurchargeDisputeSchema_> {}
